import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/types";
import type {
  Department,
  DepartmentOverview,
  DepartmentUpdate,
} from "@/api/models";
import { incidentDisplayName, toDepartment } from "@/api/models";
import { exposureStatus, isOverNioshRel } from "@/lib/carcinogen";
import { rankIncidentsByOverRel } from "@/lib/peak-gas-incidents";

export type { Department, DepartmentOverview, DepartmentUpdate };

export function useDepartments() {
  return useQuery({
    queryKey: ["departments"],
    queryFn: async () =>
      (
        await api.get<components["schemas"]["DepartmentRead"][]>("/departments")
      ).map(toDepartment),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDepartmentOverview(departmentId: string | undefined) {
  return useQuery({
    queryKey: ["department-overview", departmentId],
    queryFn: async (): Promise<DepartmentOverview> => {
      const [incidents, exposures] = await Promise.all([
        api.get<components["schemas"]["IncidentRead"][]>("/incidents"),
        api.get<components["schemas"]["ExposureRecordRead"][]>(
          "/exposure-records",
        ),
      ]);
      const monthPrefix = new Date().toISOString().slice(0, 7);
      const recent = [...incidents]
        .sort((a, b) => b.dispatched_at.localeCompare(a.dispatched_at))
        .slice(0, 8);

      // Trailing 6-month window (oldest → newest), seeded with zero so months
      // without exposure still render a (empty) bar and the axis stays even.
      // Two distinct series per month — far more valuable to a chief tracking
      // cancer risk than a raw incident count:
      //
      //   • CARCINOGEN (`total`/`voc`) — firefighter-exposures with elevated TOTAL
      //     VOC that month. Total VOC is the unspeciated aromatic load the PID
      //     measures; we use it directly as the carcinogen indicator (no benzene
      //     or single-species claim). One count per firefighter per incident.
      //   • ACUTE CO (`co`) — exposures where CO went over its NIOSH REL that
      //     month, judged on the measured incident TWA where the dose engine
      //     populated it (else a peak ÷ REL screen). CO poisoning is an ACUTE
      //     hazard, reported separately — NOT part of the carcinogen count.
      //
      // A COUNT (not a summed %REL) keeps the unit interpretable and stands up to
      // a safety officer. Same logic as the per-incident exposure table
      // (exposureStatus), so the trend reconciles with it. See src/lib/carcinogen.ts.
      const TREND_MONTHS = 6;
      const now = new Date();
      const coByMonth = new Map<string, number>();
      const vocByMonth = new Map<string, number>();
      // `total` is the carcinogen headline = the total-VOC count (== `voc`); `co`
      // is the separate acute-CO count. The chart plots `total` (carcinogen) and
      // `co` (acute) as distinct, non-stacked series — they are different hazards.
      const totalByMonth = new Map<string, number>();
      for (let k = TREND_MONTHS - 1; k >= 0; k--) {
        const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - k, 1));
        const m = d.toISOString().slice(0, 7);
        coByMonth.set(m, 0);
        vocByMonth.set(m, 0);
        totalByMonth.set(m, 0);
      }
      for (const e of exposures) {
        const when = e.finalized_at ?? e.created_at;
        const m = when.slice(0, 7);
        if (!coByMonth.has(m)) continue;
        // SAME logic as the per-incident Exposure record table (exposureStatus):
        // carcinogen = total VOC over its screen; CO over its NIOSH REL is an
        // ACUTE hazard tracked separately (TWA-judged where the dose engine has
        // populated it, else a peak screen). H₂S/LEL are acute too and excluded
        // from this carcinogen-focused trend.
        const status = exposureStatus(e);
        const co = status.coOver;
        const voc = status.vocOver;
        if (co) coByMonth.set(m, (coByMonth.get(m) ?? 0) + 1);
        if (voc) vocByMonth.set(m, (vocByMonth.get(m) ?? 0) + 1);
        if (status.carcinogenOver)
          totalByMonth.set(m, (totalByMonth.get(m) ?? 0) + 1);
      }
      const monthly_trends = Array.from(coByMonth.keys()).map((month) => ({
        month,
        label: new Date(`${month}-01T00:00:00Z`).toLocaleDateString("en-CA", {
          month: "short",
          timeZone: "UTC",
        }),
        co: coByMonth.get(month) ?? 0,
        voc: vocByMonth.get(month) ?? 0,
        total: totalByMonth.get(month) ?? 0,
      }));

      // The over-REL readings drive the "Peak gas exposures" KPI. Break them down
      // by incident — most contributors first — so clicking the KPI lists the
      // exact incidents that add up to the number, each openable. Join to the
      // incident for a human label + dispatch time (fall back to a short id when
      // the incident is outside the fetched page).
      const overRel = exposures.filter(isOverNioshRel);
      const incidentById = new Map(incidents.map((i) => [i.id, i]));
      const peak_gas_incidents = rankIncidentsByOverRel(
        overRel,
        (id) => incidentById.get(id)?.dispatched_at ?? "",
      ).map(({ incident_id, count }) => {
        const incident = incidentById.get(incident_id);
        return {
          incident_id,
          label: incident
            ? incidentDisplayName(incident)
            : `Incident ${incident_id.slice(0, 8)}`,
          count,
          dispatched_at: incident?.dispatched_at ?? "",
        };
      });

      return {
        kpis: {
          incidents_this_month: incidents.filter((i) =>
            i.dispatched_at.startsWith(monthPrefix),
          ).length,
          // Only interior_seconds is populated by the finalizer (overhaul/
          // exterior are structurally 0 — no zone telemetry on the pilot path),
          // so sum that alone rather than implying a phase split that isn't there.
          sensor_minutes_recorded: exposures.reduce(
            (sum, e) => sum + Math.round(e.interior_seconds / 60),
            0,
          ),
          // "Peak readings over a NIOSH limit (screen)" — count records whose CO,
          // H2S, or VOC peak reaches its NIOSH limit: a screening flag, not a
          // confirmed dose exceedance, and not merely any non-zero reading. Same
          // limits as the per-incident exposure table (src/lib/carcinogen.ts).
          peak_gas_exposures: overRel.length,
          peak_gas_incidents,
          open_incidents: incidents.filter((i) => i.status === "active").length,
        },
        monthly_trends,
        recent_activity: recent.map((i) => ({
          id: i.id,
          // Name incidents the same way the Incidents tab does (type · address),
          // not the opaque UUID — incident_number is usually null until CAD assigns one.
          label: incidentDisplayName(i),
          ts: i.dispatched_at,
          link: `/incidents/${i.id}`,
        })),
      };
    },
    enabled: !!departmentId,
    // The overview + carcinogen-insights dashboards must reflect every
    // just-closed incident (exposure_record is written at incident close). So
    // refetch on every page open (no stale window) and when the chief returns to
    // the browser after closing a call in the iOS app — the global QueryClient
    // sets staleTime 30 s + refetchOnWindowFocus false, which would otherwise
    // serve a stale carcinogen count.
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
}

export function useUpdateDepartment(departmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: DepartmentUpdate) =>
      api.patch<Department>(`/departments/${departmentId}`, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["departments"] });
    },
  });
}

export type ProvisionChiefInput = {
  name: string;
  jurisdiction: string;
  timezone: string;
  chief_email: string;
  chief_given_name: string;
  chief_family_name: string;
};

export function useProvisionChief() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProvisionChiefInput) =>
      api.post<{
        department_id: string;
        chief: components["schemas"]["UserRead"];
      }>("/departments/provision-chief", input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["departments"] });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}
